import { type TaskCreateDto, type TaskPriority, type TaskStatus } from "@crowdcoursing/api/openapi";
import { Flag } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  TextField as MuiTextField,
} from "@mui/material";
import { DateTime } from "luxon";
import { Field, Form } from "react-final-form";

import { TextField } from "~ui/atoms/Form/TextField/TextField";

const TASK_PRIORITIES = [
  { label: "Urgent", value: 1 },
  { label: "High", value: 2 },
  { label: "Medium", value: 3 },
  { label: "Low", value: 4 },
];

const tAddTask = "Add Task";
const tCancel = "Cancel";
const tAdd = "Add";
const tTitle = "Title";
const tDescription = "Description";
const tPriority = "Priority";
const tDueDate = "Due Date";
const tReminderDate = "Reminder Date";

interface AddTaskModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TaskCreateDto) => Promise<void>;
}

export const AddTaskModal = ({ open, onClose, onSubmit }: AddTaskModalProps) => {
  const handleSubmit = async (values: TaskCreateDto) => {
    // Add timezone offset to dates before submitting
    const formattedValues = {
      ...values,
      dueAt: values.dueAt
        ? DateTime.fromFormat(values.dueAt, "yyyy-MM-dd'T'HH:mm").toFormat("yyyy-MM-dd'T'HH:mmZZ")
        : values.dueAt,
      reminderAt: values.reminderAt
        ? DateTime.fromFormat(values.reminderAt, "yyyy-MM-dd'T'HH:mm").toFormat("yyyy-MM-dd'T'HH:mmZZ")
        : values.reminderAt,
    };
    await onSubmit(formattedValues);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Form
        onSubmit={handleSubmit}
        initialValues={{
          dueAt: DateTime.now()
            .plus({ days: 1 })
            .set({ hour: 12, millisecond: 0, minute: 0, second: 0 })
            .toFormat("yyyy-MM-dd'T'HH:mm"),
          priority: 3 as TaskPriority, // Start with Medium priority
          status: 1 as TaskStatus,
        }}
        render={({ handleSubmit, submitting, hasValidationErrors, errors }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle>{tAddTask}</DialogTitle>
            <DialogContent>
              <Box display="grid" gap={2} py={1}>
                <TextField
                  name="title"
                  required
                  validate={(value) => (!value ? "Required" : undefined)}
                  textFieldProps={{
                    label: tTitle,
                  }}
                />
                <TextField
                  name="description"
                  textFieldProps={{
                    label: tDescription,
                    multiline: true,
                    rows: 4,
                  }}
                />
                <Field name="priority">
                  {({ input }) => (
                    <FormControl fullWidth>
                      <InputLabel id="priority-label">{tPriority}</InputLabel>
                      <MuiSelect {...input} labelId="priority-label" label={tPriority}>
                        {TASK_PRIORITIES.map((priority) => (
                          <MenuItem key={priority.value} value={priority.value}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Flag
                                sx={{
                                  color:
                                    priority.value === 1
                                      ? "#d32f2f" // Urgent - Deeper red
                                      : priority.value === 2
                                        ? "#f57c00" // High - Orange
                                        : priority.value === 3
                                          ? "warning.main" // Medium - Yellow
                                          : "primary.main", // Low
                                }}
                                fontSize="small"
                              />
                              {priority.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </MuiSelect>
                    </FormControl>
                  )}
                </Field>
                <Field name="dueAt">
                  {({ input }) => (
                    <MuiTextField
                      {...input}
                      required
                      fullWidth
                      label={tDueDate}
                      type="datetime-local"
                      slotProps={{
                        inputLabel: { shrink: true },
                      }}
                    />
                  )}
                </Field>
                <Field name="reminderAt">
                  {({ input }) => (
                    <MuiTextField
                      {...input}
                      fullWidth
                      label={tReminderDate}
                      type="datetime-local"
                      slotProps={{
                        inputLabel: { shrink: true },
                      }}
                    />
                  )}
                </Field>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>{tCancel}</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || hasValidationErrors || Object.keys(errors || {}).length > 0}
              >
                {tAdd}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};
